import { useState } from "react";
import { Link } from "react-router";
import { useTranslation } from "react-i18next";
import { Mail, CheckCircle2 } from "lucide-react";
import { AuthLayout } from "../../../auth";
import { sendPatientPasswordReset } from "../../application/portalAccount";

export default function PortalForgotPasswordPage() {
  const { t, i18n } = useTranslation();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await sendPatientPasswordReset(email, i18n.language.slice(0, 2));
      setSent(true);
    } catch {
      setError(t("portal.forgotPassword.error"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout
      panelTagline={t("portal.login.panelTagline")}
      panelTitle={t("portal.login.panelTitle")}
      colorScheme="portal"
    >
      <div className="space-y-8">
        <div>
          <h1 className="text-3xl font-bold text-content-primary mb-2">
            {t("portal.forgotPassword.title")}
          </h1>
          <p className="text-content-tertiary">
            {t("portal.forgotPassword.subtitle")}
          </p>
        </div>

        {sent ? (
          <div className="p-4 rounded-2xl bg-green-50 dark:bg-green-500/10 border border-green-200 dark:border-green-500/20 text-green-700 dark:text-green-400 text-sm flex gap-3 items-center">
            <CheckCircle2 className="w-5 h-5 shrink-0" />
            {t("portal.forgotPassword.sent")}
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="p-4 rounded-2xl bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 text-red-600 dark:text-red-400 text-sm">
                {error}
              </div>
            )}

            <div className="space-y-2">
              <label className="block text-sm font-medium text-content-secondary">
                {t("portal.login.emailLabel")}
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-content-tertiary" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                  className="w-full rounded-xl border border-gray-200 dark:border-gray-700 dark:bg-gray-800 pl-10 pr-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                  placeholder={t("portal.login.emailPlaceholder")}
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading || !email}
              className="w-full rounded-xl bg-brand-600 hover:bg-brand-700 disabled:opacity-50 text-white font-semibold py-3 transition-colors"
            >
              {loading ? t("portal.forgotPassword.loading") : t("portal.forgotPassword.submit")}
            </button>
          </form>
        )}

        <div className="pt-4 border-t border-gray-100 dark:border-white/[0.05] text-center">
          <Link
            to="/portal/login"
            className="text-sm font-bold text-brand-600 dark:text-brand-400 hover:underline"
          >
            {t("portal.forgotPassword.backToLogin")}
          </Link>
        </div>
      </div>
    </AuthLayout>
  );
}
